/**
 * Fibromyalgia Tender Point Map — HIGH #9
 *
 * Standard 18-point fibromyalgia tender map (9 bilateral pairs).
 * Renders as labeled checkboxes grouped by left/right side.
 * Satisfies both canvas-accessibility and fibromyalgia-specific workflow.
 */

import React from 'react';

const TENDER_POINTS = [
  { id: 'occipital', label: 'Occipital (base of skull)', side: 'bilateral' },
  { id: 'low_cervical', label: 'Low Cervical (front of neck, C5-C7)', side: 'bilateral' },
  { id: 'trapezius', label: 'Trapezius (midpoint of upper border)', side: 'bilateral' },
  { id: 'supraspinatus', label: 'Supraspinatus (above shoulder blade)', side: 'bilateral' },
  { id: 'second_rib', label: 'Second Rib (upper chest at costochondral junction)', side: 'bilateral' },
  { id: 'lateral_epicondyle', label: 'Lateral Epicondyle (outer elbow)', side: 'bilateral' },
  { id: 'gluteal', label: 'Gluteal (upper outer quadrant of buttock)', side: 'bilateral' },
  { id: 'greater_trochanter', label: 'Greater Trochanter (hip)', side: 'bilateral' },
  { id: 'knee', label: 'Knee (medial fat pad, proximal to joint line)', side: 'bilateral' },
];

const FibromyalgiaTenderPoints = ({ selectedPoints = [], onChange }) => {
  const handleToggle = (pointId, side) => {
    const key = `${pointId}_${side}`;
    const updated = selectedPoints.includes(key)
      ? selectedPoints.filter((p) => p !== key)
      : [...selectedPoints, key];
    onChange?.(updated);
  };

  const leftPoints = TENDER_POINTS.map((p) => ({ ...p, sideLabel: 'Left', key: `${p.id}_left` }));
  const rightPoints = TENDER_POINTS.map((p) => ({ ...p, sideLabel: 'Right', key: `${p.id}_right` }));

  return (
    <div className="fibro-tender-container">
      <div style={{ marginBottom: '12px' }}>
        <p style={{ fontSize: '13px', color: '#666', margin: 0 }}>
          Standard 18-point fibromyalgia tender point assessment. Select all tender points.
          ({selectedPoints.length} of 18 selected)
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        {/* Left side */}
        <fieldset className="pain-checklist-group">
          <legend className="pain-checklist-legend">Left Side (9 points)</legend>
          {leftPoints.map((point) => {
            const isSelected = selectedPoints.includes(point.key);
            return (
              <label key={point.key} className={`pain-checklist-item ${isSelected ? 'selected' : ''}`}>
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => handleToggle(point.id, 'left')}
                  aria-label={`Left ${point.label}`}
                  className="pain-checklist-checkbox"
                />
                <span className="pain-checklist-label">{point.label}</span>
              </label>
            );
          })}
        </fieldset>

        {/* Right side */}
        <fieldset className="pain-checklist-group">
          <legend className="pain-checklist-legend">Right Side (9 points)</legend>
          {rightPoints.map((point) => {
            const isSelected = selectedPoints.includes(point.key);
            return (
              <label key={point.key} className={`pain-checklist-item ${isSelected ? 'selected' : ''}`}>
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => handleToggle(point.id, 'right')}
                  aria-label={`Right ${point.label}`}
                  className="pain-checklist-checkbox"
                />
                <span className="pain-checklist-label">{point.label}</span>
              </label>
            );
          })}
        </fieldset>
      </div>

      {selectedPoints.length > 0 && (
        <div className="pain-checklist-summary" aria-live="polite" style={{ marginTop: '12px' }}>
          <strong>{selectedPoints.length} of 18 tender points selected</strong>
          {selectedPoints.length >= 11 && (
            <span style={{ color: '#dc2626', marginLeft: '8px' }}>
              (Meets ACR criteria: 11+ of 18)
            </span>
          )}
        </div>
      )}
    </div>
  );
};

FibromyalgiaTenderPoints.TENDER_POINTS = TENDER_POINTS;

export default FibromyalgiaTenderPoints;
